import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import { SxProps, Theme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { t } from 'i18next';
import { FieldValues, Path, useController, UseControllerProps } from 'react-hook-form';

import Label from './Label';

const OUTLINE_WIDTH_IN_PX = 1;

type ToggleButtonOption<V> = {
  label: string;
  value: V;
  isDisabled?: boolean;
};

type ToggleButtonGroupProps<V> = {
  error?: string;
  isDisabled?: boolean;
  isRequired?: boolean;
  label?: React.ReactNode;
  onChange: (value: V[]) => void;
  options: ToggleButtonOption<V>[];
  sx?: SxProps<Theme>;
  value: V[];
};

function ToggleButtonGroup<V>({
  error,
  isDisabled,
  isRequired,
  label,
  onChange,
  options,
  sx,
  value,
}: ToggleButtonGroupProps<V>) {
  const handleToggle = (option: ToggleButtonOption<V>) => {
    if (isDisabled || option.isDisabled) {
      return;
    }

    const isSelected = value.some((item) => item === option.value);
    const next = isSelected
      ? value.filter((item) => item !== option.value)
      : [...value, option.value];

    onChange(next);
  };

  return (
    <FormControl
      disabled={isDisabled}
      error={Boolean(error)}
      sx={[
        { display: 'flex', flexDirection: 'column', gap: 0.25 },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {label && <Label isRequired={isRequired}>{label}</Label>}
      <Box
        sx={{
          alignItems: 'center',
          display: 'flex',
          position: 'relative',
        }}
      >
        {options.map((option, index) => {
          const isSelected = value.some((item) => item === option.value);

          return (
            <ToggleButton
              key={String(option.value)}
              isDisabled={isDisabled || option.isDisabled}
              isFirstOption={index === 0}
              isLastOption={index === options.length - 1}
              isSelected={isSelected}
              label={option.label}
              onClick={() => handleToggle(option)}
            />
          );
        })}
      </Box>
      {error && (
        <FormHelperText sx={{ m: 0, fontSize: '10px', fontWeight: 700 }}>
          {error}
        </FormHelperText>
      )}
    </FormControl>
  );
}

type RhfProps<T extends FieldValues, V> = Omit<UseControllerProps<T>, 'name'> & {
  isDisabled?: boolean;
  isRequired?: boolean;
  label?: React.ReactNode;
  name: Path<T>;
  options: ToggleButtonOption<V>[];
  sx?: SxProps<Theme>;
};

export default function ToggleButtonInput<T extends FieldValues, V>({
  control,
  isDisabled,
  isRequired,
  label,
  name,
  options,
  sx,
}: RhfProps<T, V>) {
  const { field, fieldState } = useController({ control, name });
  const error = fieldState.error?.message ?? fieldState.error?.root?.message;

  const selected = (field.value ?? []) as ToggleButtonOption<V>[];
  const selectedValues = selected.map((item) => item.value);

  const handleChange = (nextValues: V[]) => {
    const next = nextValues.map((v) => {
      const existing = selected.find((item) => item.value === v);
      if (existing) {
        return existing;
      }
      const option = options.find((o) => o.value === v);
      return { label: option?.label ?? String(v), value: v };
    });
    field.onChange(next);
  };

  return (
    <ToggleButtonGroup
      error={error}
      isDisabled={isDisabled}
      isRequired={isRequired}
      label={label}
      onChange={handleChange}
      options={options}
      sx={sx}
      value={selectedValues}
    />
  );
}

type InviteToggleKey = 'email' | 'sms';

type InvitesToSendShape = {
  email: { shouldSend: boolean; isDisabled: boolean };
  sms: { shouldSend: boolean; isDisabled: boolean };
};

type InvitesToSendToggleGroupProps = {
  invitesToSend: InvitesToSendShape | undefined;
  isUpdating?: boolean;
  label?: React.ReactNode;
  onChangeInvite: (event: React.ChangeEvent<HTMLInputElement>) => void;
  sx?: SxProps<Theme>;
};

export function InvitesToSendToggleGroup({
  invitesToSend,
  isUpdating,
  label,
  onChangeInvite,
  sx,
}: InvitesToSendToggleGroupProps) {
  if (!invitesToSend) {
    return null;
  }

  const value: InviteToggleKey[] = [
    ...(invitesToSend.email.shouldSend ? (['email'] as const) : []),
    ...(invitesToSend.sms.shouldSend ? (['sms'] as const) : []),
  ];

  const options: ToggleButtonOption<InviteToggleKey>[] = [
    {
      label: t('enums.invite_type.email'),
      value: 'email',
      isDisabled: Boolean(isUpdating) || invitesToSend.email.isDisabled,
    },
    {
      label: t('enums.invite_type.sms'),
      value: 'sms',
      isDisabled: Boolean(isUpdating) || invitesToSend.sms.isDisabled,
    },
  ];

  const emit = (name: InviteToggleKey, checked: boolean) => {
    onChangeInvite({
      target: { name, checked },
    } as React.ChangeEvent<HTMLInputElement>);
  };

  const handleChange = (next: InviteToggleKey[]) => {
    const prev = new Set(value);
    const nextSet = new Set(next);
    (['email', 'sms'] as InviteToggleKey[]).forEach((key) => {
      if (prev.has(key) !== nextSet.has(key)) {
        emit(key, nextSet.has(key));
      }
    });
  };

  return (
    <ToggleButtonGroup
      label={label ?? t('account.form.invite_type')}
      onChange={handleChange}
      options={options}
      sx={sx}
      value={value}
    />
  );
}

interface ToggleButtonProps {
  isDisabled?: boolean;
  isFirstOption: boolean;
  isLastOption: boolean;
  isSelected: boolean;
  label: string;
  onClick: () => void;
}

function ToggleButton({
  isDisabled,
  isFirstOption,
  isLastOption,
  isSelected,
  label,
  onClick,
}: ToggleButtonProps) {
  const theme = useTheme();

  return (
    <Box
      onClick={isDisabled ? undefined : onClick}
      sx={{
        alignItems: 'center',
        bgcolor: isSelected
          ? theme.brandV2.colors.treadGray1
          : theme.semantic.surfaceRaised,
        display: 'flex',
        height: '100%',
        justifyContent: 'center',
        p: theme.spacing(0.75, 1.5),
        position: 'relative',
        cursor: isDisabled ? 'not-allowed' : 'pointer',
        mr: isSelected ? '-1px' : 0,
        opacity: isDisabled ? 0.6 : 1,

        outline: `solid ${OUTLINE_WIDTH_IN_PX}px ${
          isSelected ? theme.brandV2.colors.treadGray1 : theme.brandV2.colors.treadGray7
        }`,

        userSelect: 'none',
        zIndex: isSelected ? 1 : 0,

        ...(isFirstOption
          ? {
              borderBottomLeftRadius: theme.brandV2.borderRadius,
              borderTopLeftRadius: theme.brandV2.borderRadius,
            }
          : { ml: '1px' }),

        ...(isLastOption
          ? {
              borderBottomRightRadius: theme.brandV2.borderRadius,
              borderTopRightRadius: theme.brandV2.borderRadius,
            }
          : {}),
      }}
    >
      <Typography
        component="span"
        variant="subtitle2"
        sx={{
          color: isSelected
            ? theme.semantic.surfaceRaised
            : theme.brandV2.colors.treadBlack,
          whiteSpace: 'nowrap',
        }}
      >
        {label}
      </Typography>
    </Box>
  );
}
